import React, { useEffect, useState } from "react";
import { Card, Space, Row, Col, Table, notification, Tag } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { EvaluationService } from "../../services/EvaluationService";
import { usePermissions } from "../../PermissionsProvider";
import { PrimaryButton } from "../shared/button/Buttons";
import { EvaluationRun } from "../../model_gen/evaluation";

type EvaluationColumns = ColumnsType<EvaluationRun>;

const EvaluationHome: React.FC = () => {
  const { apiKey } = usePermissions();
  const [evaluations, setEvaluations] = useState<EvaluationRun[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const navigate = useNavigate();

  useEffect(() => {
    refreshEvaluations();
  }, [apiKey]);

  const refreshEvaluations = () => {
    setIsLoading(true);
    EvaluationService.getEvaluationRuns(setIsLoading, apiKey).subscribe(
      (evaluations: EvaluationRun[]) => {
        const sortedEvaluations = evaluations.sort((a: EvaluationRun, b: EvaluationRun) => moment(b.date_start).unix() - moment(a.date_start).unix());
        setEvaluations(sortedEvaluations);
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch evaluations: ${error}`,
        });
        setIsLoading(false);
      }
    );
  };

  const evaluationColumns: EvaluationColumns = [
    {
      title: "Application",
      dataIndex: ["application", "application_name"],
      key: "application_name",
      sorter: (a, b) => (a.application?.application_name ?? "").localeCompare(b.application?.application_name ?? ""),
    },
    {
      title: "Intent",
      dataIndex: ["intent", "action_type"],
      key: "intent",
      sorter: (a, b) => (a.intent?.action_type ?? "").localeCompare(b.intent?.action_type ?? ""),
    },
    {
      title: "Date Start",
      dataIndex: "date_start",
      key: "date_start",
      render: (date) => moment.unix(date).format("YYYY-MM-DD"),
      sorter: (a, b) => (a.date_start ?? 0) - (b.date_start ?? 0),
    },
    {
      title: "Date End",
      dataIndex: "date_end",
      key: "date_end",
      render: (date) => moment.unix(date).format("YYYY-MM-DD"),
      sorter: (a, b) => (a.date_end ?? 0) - (b.date_end ?? 0),
    },
    {
      title: "Requests",
      dataIndex: "request_count",
      key: "request_count",
      sorter: (a, b) => (a.request_count ?? 0) - (b.request_count ?? 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const tagProps = {
          PENDING: { color: "default" },
          ERROR: { color: "error" },
          DONE: { color: "success" },
        }[status] || { color: "default" };
        
        return <Tag {...tagProps}>{status}</Tag>;
      },
      sorter: (a, b) => (a.status ?? "").localeCompare(b.status ?? ""),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  const paginatedEvaluations = evaluations.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <Card className="applications-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Evaluations</h1>
          </Col>
          <Col>
            <PrimaryButton
              title="New Evaluation"
              className="buttonWrapper"
              icon={<PlusOutlined />}
              onClick={() => navigate("/evaluation/new")}
            />
          </Col>
        </Row>
        <Table
          loading={isLoading}
          rowKey={(record) => record.id.toString()}
          columns={evaluationColumns}
          dataSource={paginatedEvaluations}
          pagination={
            evaluations.length > pageSize
              ? {
                  current: currentPage,
                  pageSize: pageSize,
                  total: evaluations.length,
                  showSizeChanger: false,
                }
              : false
          }
          onChange={handleTableChange}
          onRow={(record) => {
            return {
              onClick: () => navigate(`/evaluation/${record.id}`),
            };
          }}
        />
      </Space>
    </Card>
  );
};

export default EvaluationHome;
